import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs/operators';
import { ApiMethod } from '../../apimethods/api-method';

interface PraTatRecord {
  Id: number;
  EntryDate: string | Date;
  UploadedFile: string;
  WmsExcel?: string | null;
  ProcessedFile?: string | null;
}

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
}

@Component({
  selector: 'app-pra-tat-process',
  templateUrl: './pra_tat_process.component.html',
  styleUrls: ['./pra_tat_process.component.css']
})
export class PraTatProcessComponent implements OnInit {

  selectedFile: File | null = null;
  records: PraTatRecord[] = [];

  isUploading = false;
  isLoading = false;
  isLoadingList = false;
  isValidating = false;
  dragOver = false;

  progress = 0;
  maxFileSizeMB = 50;

  successMessage = '';
  errorMessage = '';
  searchText = '';

  selectedMonth = '';
  selectedYear = new Date().getFullYear();

  months = [
    { value: 'Jan', label: 'Jan' },
    { value: 'Feb', label: 'Feb' },
    { value: 'Mar', label: 'Mar' },
    { value: 'Apr', label: 'Apr' },
    { value: 'May', label: 'May' },
    { value: 'Jun', label: 'Jun' },
    { value: 'Jul', label: 'Jul' },
    { value: 'Aug', label: 'Aug' },
    { value: 'Sep', label: 'Sep' },
    { value: 'Oct', label: 'Oct' },
    { value: 'Nov', label: 'Nov' },
    { value: 'Dec', label: 'Dec' }
  ];

  years = Array.from(
    { length: 6 },
    (_, i) => new Date().getFullYear() - i
  );

  constructor() { }

  ngOnInit(): void {
    this.loadPraTatList();
  }

  // Filter uploaded records
  get filteredRecords(): PraTatRecord[] {
    const search = this.searchText.trim().toLowerCase();

    if (!search) {
      return this.records;
    }

    return this.records.filter(record =>
      String(record.Id ?? '').toLowerCase().includes(search) ||
      String(record.UploadedFile ?? '').toLowerCase().includes(search) ||
      String(record.WmsExcel ?? '').toLowerCase().includes(search) ||
      String(record.ProcessedFile ?? '').toLowerCase().includes(search)
    );
  }

  // Format selected file size
  get formattedSize(): string {
    if (!this.selectedFile) {
      return '';
    }

    const size = this.selectedFile.size;

    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }

    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  }

  // Select, validate, and automatically upload ZIP file
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    this.successMessage = '';
    this.errorMessage = '';

    if (this.isUploading) {
      return;
    }

    const file = input.files?.[0];

    if (!file) {
      return;
    }

    const startTime = performance.now();

    // Validate file extension without reading its contents
    if (!file.name.toLowerCase().endsWith('.zip')) {
      this.errorMessage = 'Please select a ZIP file.';
      input.value = '';
      return;
    }

    // Validate file size
    const maxFileSize = this.maxFileSizeMB * 1024 * 1024;

    if (file.size > maxFileSize) {
      this.errorMessage =
        `File size must not exceed ${this.maxFileSizeMB} MB.`;
      input.value = '';
      return;
    }

    // Validate empty file
    if (file.size === 0) {
      this.errorMessage = 'The selected file is empty.';
      input.value = '';
      return;
    }

    // Assign file without reading or extracting ZIP contents
    this.selectedFile = file;
    this.progress = 0;

    console.log(
      `ZIP selection and validation completed in ${(performance.now() - startTime).toFixed(2)
      } ms`
    );

    // Start upload automatically
    this.uploadFile();
  }

  // Drag and drop handlers
  onDragOver(event: DragEvent): void {
    event.preventDefault();

    if (!this.isUploading && !this.isValidating) {
      this.dragOver = true;
    }
  }

  onDragLeave(): void {
    this.dragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;

    if (this.isUploading || this.isValidating) {
      return;
    }

    const file = event.dataTransfer?.files?.[0];

    if (!file) {
      return;
    }

    const input = document.getElementById(
      'praTatZipFile'
    ) as HTMLInputElement | null;

    if (!input) {
      this.errorMessage = 'File input element was not found.';
      return;
    }

    const transfer = new DataTransfer();
    transfer.items.add(file);
    input.files = transfer.files;

    // Use the same validation and upload flow
    this.onFileSelected({
      target: input
    } as unknown as Event);
  }

  // Retained for compatibility with existing HTML
  upload(): void {
    this.uploadFile();
  }

  // Upload ZIP file
  uploadFile(): void {
    if (this.isUploading) {
      return;
    }

    if (!this.selectedMonth || !this.selectedYear) {
      this.errorMessage = 'Please select both Month and Year.';
      return;
    }

    if (!this.selectedFile) {
      this.errorMessage = 'Please select a ZIP file before uploading.';
      return;
    }

    const formData = new FormData();

    formData.append(
      'file',
      this.selectedFile,
      this.selectedFile.name
    );

    formData.append(
      'loginId',
      String(localStorage.aLoginID)
    );

    formData.append(
      'PublisherId',
      String(localStorage.aPubID)
    );

    formData.append(
      'MonthYear',
      `${this.selectedMonth} ${this.selectedYear}`
    );

    this.isUploading = true;
    this.progress = 0;
    this.successMessage = '';
    this.errorMessage = '';

    ApiMethod.postFileMethod1(
      'api/UploadPraTatProcess',
      formData
    )
      .pipe(
        finalize(() => {
          this.isUploading = false;
        })
      )
      .subscribe({
        next: (response: any) => {
          if (response?.success) {
            this.successMessage =
              response.message ||
              'PRA TAT process started successfully.';

            this.selectedFile = null;
            this.progress = 100;
            this.resetFileInput();

            // Refresh records after acknowledgement
            this.loadPraTatList();
          } else {
            this.errorMessage =
              response?.message ||
              'ZIP file upload failed.';
          }
        },

        error: (error: any) => {
          this.errorMessage =
            error?.error?.message ||
            error?.error?.Message ||
            error?.message ||
            'An error occurred while uploading the ZIP file.';
        }
      });
  }

  // Retrieve uploaded ZIP records
  loadPraTatList(): void {
    this.isLoading = true;
    this.isLoadingList = true;

    const dataParam = {
      LoginId: localStorage.aLoginID,
      PublisherId: localStorage.aPubID
    };

    ApiMethod.postMethod(
      'api/GetPraTatList',
      dataParam
    )
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.isLoadingList = false;
        })
      )
      .subscribe({
        next: (response: ApiResponse<PraTatRecord[]>) => {
          if (response?.success) {
            this.records = response.data || [];
          } else {
            this.errorMessage =
              response?.message ||
              'Unable to retrieve PRA TAT records.';
          }
        },

        error: (error: HttpErrorResponse) => {
          this.errorMessage = this.getErrorMessage(
            error,
            'An error occurred while retrieving PRA TAT records.'
          );
        }
      });
  }

  // Refresh records
  refreshList(): void {
    this.errorMessage = '';
    this.loadPraTatList();
  }

  // Remove selected file
  removeFile(): void {
    this.clearSelectedFile();
  }

  clearSelectedFile(): void {
    if (this.isUploading) {
      return;
    }

    this.selectedFile = null;
    this.progress = 0;
    this.successMessage = '';
    this.errorMessage = '';

    this.resetFileInput();
  }

  // Reset HTML file input
  private resetFileInput(): void {
    const input = document.getElementById(
      'praTatZipFile'
    ) as HTMLInputElement | null;

    if (input) {
      input.value = '';
    }
  }

  // API error message handler
  private getErrorMessage(
    error: HttpErrorResponse,
    fallback: string
  ): string {
    if (error?.error && typeof error.error === 'object') {
      return error.error.message ||
        error.error.Message ||
        fallback;
    }

    if (
      typeof error?.error === 'string' &&
      error.error.trim()
    ) {
      return error.error;
    }

    return error?.message || fallback;
  }

  // Download uploaded, WMS, or processed file
  downloadFile(
    record: PraTatRecord,
    fileType: 'uploaded' | 'wms' | 'processed'
  ): void {
    ApiMethod.Downloadfile(
      `/api/pratat/download?id=${record.Id}&fileType=${fileType}`
    );
  }


  // Pagination
  pageIndex = 0;
  pageSize = 25;

  // Required for Math.min in the HTML template
  Math = Math;

  // Calculate total pages
  get totalPages(): number {
    return Math.max(
      1,
      Math.ceil(this.filteredRecords.length / this.pageSize)
    );
  }

  // Records for the current page
  get paginatedRecords(): PraTatRecord[] {
    const startIndex = this.pageIndex * this.pageSize;

    return this.filteredRecords.slice(
      startIndex,
      startIndex + this.pageSize
    );
  }

  // Navigate to previous page
  previousPage(): void {
    if (this.pageIndex > 0) {
      this.pageIndex--;
    }
  }

  // Navigate to next page
  nextPage(): void {
    if (this.pageIndex < this.totalPages - 1) {
      this.pageIndex++;
    }
  }

  // Change page size
  changePageSize(size: number): void {
    this.pageSize = Number(size);
    this.pageIndex = 0;
  }

  // Reset pagination after loading records
  // Add this line after assigning this.records in loadPraTatList():
  // this.pageIndex = 0;
}
